/**
 * Recording of the live browser view into a clip.
 *
 * Adapted from t3code (`apps/web/src/browser/browserRecording.ts` and
 * `browserRecordingScope.ts`, MIT, pin `e3e7cc3f`). Forge delta: upstream
 * records a desktop screencast through the main process and uploads the
 * result; forge records the WebRTC live view's stream directly in the client
 * and hands the clip to the composer as a file attachment. One recording at
 * a time, the conflict rule upstream's scope module owns. The live view is
 * the only recordable surface, so the recording is tagged with the tab the
 * requester named rather than per-video-element.
 *
 * @module browserRecording
 */
import { create } from "zustand";

import { randomUUID } from "~/lib/utils";

const RECORDING_MIME_CANDIDATES = [
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
] as const;

export class BrowserRecordingUnavailableError extends Error {
  constructor(
    readonly tabId: string,
    reason: string,
  ) {
    super(`Browser recording is unavailable for tab ${tabId}: ${reason}`);
    this.name = "BrowserRecordingUnavailableError";
  }
}

export class BrowserRecordingConflictError extends Error {
  constructor(
    readonly requestedTabId: string,
    readonly activeTabId: string,
  ) {
    super(
      `Cannot record tab ${requestedTabId} while tab ${activeTabId} is already being recorded.`,
    );
    this.name = "BrowserRecordingConflictError";
  }
}

interface BrowserRecordingStoreState {
  /** Tabs with a recording in progress. */
  readonly byTabId: Record<string, { readonly startedAt: string }>;
  readonly begin: (tabId: string) => void;
  readonly end: (tabId: string) => void;
}

export const useBrowserRecordingStore = create<BrowserRecordingStoreState>()((set) => ({
  byTabId: {},
  begin: (tabId) =>
    set((state) => ({
      byTabId: { ...state.byTabId, [tabId]: { startedAt: new Date().toISOString() } },
    })),
  end: (tabId) =>
    set((state) => {
      if (!(tabId in state.byTabId)) return state;
      const { [tabId]: _ended, ...byTabId } = state.byTabId;
      return { byTabId };
    }),
}));

export function isBrowserRecordingSupported(): boolean {
  return typeof MediaRecorder !== "undefined";
}

/** Whether the element's stream still has a live video track to record. */
function browserVideoHasLiveTrack(element: HTMLVideoElement): boolean {
  const stream = element.srcObject as MediaStream | null;
  return Boolean(stream?.getVideoTracks().some((track) => track.readyState === "live"));
}

/**
 * Whether the element streams the requested tab. Live surfaces carry their
 * tab on the nearest `[data-browser-tab-id]` ancestor, so a different-tab
 * floater beside the panel can never be mistaken for the tab the recording
 * names. Without a tabId, any live surface qualifies.
 */
function browserVideoStreamsTab(element: HTMLVideoElement, tabId: string): boolean {
  const owner = element.closest<HTMLElement>("[data-browser-tab-id]");
  return owner?.getAttribute("data-browser-tab-id") === tabId;
}

/**
 * The live view's video element for the requested tab, or null when no
 * display is rendering it. Only elements with a stream that still has a live
 * video track qualify; the panel's and the floater's stale frames do not.
 * Fail-closed on the tab: recording the wrong surface is worse than
 * recording none.
 */
export function findLiveBrowserVideo(
  root: ParentNode = document,
  tabId?: string,
): HTMLVideoElement | null {
  const videos = Array.from(
    root.querySelectorAll<HTMLVideoElement>('[data-testid="remote-webrtc-browser-video"]'),
  );
  return (
    videos.find(
      (element) =>
        (tabId === undefined || browserVideoStreamsTab(element, tabId)) &&
        browserVideoHasLiveTrack(element),
    ) ?? null
  );
}

interface ActiveRecording {
  readonly tabId: string;
  readonly recorder: MediaRecorder;
  readonly chunks: Blob[];
  readonly startedAtMs: number;
}

// A module singleton, deliberately not store state: the MediaRecorder handle
// and its chunk buffer must survive any component unmount, while
// `useBrowserRecordingStore` only mirrors the user-visible state (which tabs
// are recording).
let activeRecording: ActiveRecording | null = null;

/** The tab currently being recorded, or null. */
export function activeBrowserRecordingTabId(): string | null {
  return activeRecording?.tabId ?? null;
}

function resolveRecordingMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const mimeType of RECORDING_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mimeType)) return mimeType;
  }
  return null;
}

export async function startBrowserRecording(input: { readonly tabId: string }): Promise<void> {
  const { tabId } = input;
  if (activeRecording) {
    if (activeRecording.tabId === tabId) return;
    throw new BrowserRecordingConflictError(tabId, activeRecording.tabId);
  }
  if (!isBrowserRecordingSupported()) {
    throw new BrowserRecordingUnavailableError(tabId, "this browser cannot record video");
  }
  const mimeType = resolveRecordingMimeType();
  if (!mimeType) {
    throw new BrowserRecordingUnavailableError(tabId, "no supported recording format");
  }
  const stream = findLiveBrowserVideo(document, tabId)?.srcObject as MediaStream | null;
  if (!stream || !stream.getVideoTracks().some((track) => track.readyState === "live")) {
    throw new BrowserRecordingUnavailableError(tabId, "no live browser display is attached");
  }

  const recorder = new MediaRecorder(stream, { mimeType });
  const chunks: Blob[] = [];
  recorder.addEventListener("dataavailable", (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  });
  recorder.start(1_000);
  activeRecording = { tabId, recorder, chunks, startedAtMs: Date.now() };
  useBrowserRecordingStore.getState().begin(tabId);
}

export interface BrowserRecordingResult {
  readonly file: File;
  readonly durationMs: number;
}

/**
 * Stops the tab's recording and resolves with the clip. Null when the tab
 * was not recording; a recording stopped from under it (unmount) resolves
 * with whatever was captured.
 */
export async function stopBrowserRecording(input: {
  readonly tabId: string;
}): Promise<BrowserRecordingResult | null> {
  const recording = activeRecording;
  if (!recording || recording.tabId !== input.tabId) return null;
  activeRecording = null;
  useBrowserRecordingStore.getState().end(input.tabId);

  const recorder = recording.recorder;
  // The recorder can already be inactive: the stream ended on its own, which
  // makes stop() throw InvalidStateError and would lose everything captured
  // before it. Its final dataavailable has already landed in the chunk
  // buffer, so the clip assembles from what arrived.
  if (recorder.state !== "inactive") {
    await new Promise<void>((resolve) => {
      recorder.addEventListener("stop", () => resolve(), { once: true });
      recorder.stop();
    });
  }

  const blob = new Blob(recording.chunks, { type: recorder.mimeType || "video/webm" });
  const extension = blob.type.includes("mp4") ? "mp4" : "webm";
  const file = new File([blob], `browser-recording-${randomUUID()}.${extension}`, {
    type: blob.type,
  });
  return { file, durationMs: Date.now() - recording.startedAtMs };
}
